import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowLeft, Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Reveal from "../components/common/Reveal";
import DateField from "../components/common/DateField";
import DateRangeField from "../components/common/DateRangeField";
import AirportField from "../components/common/AirportField";
import AirlineField from "../components/common/AirlineField";
import AutocompleteField from "../components/common/AutocompleteField";
import { formatPhone, isValidEmail } from "../lib/formUtils";

const TRIP_TYPES = [
  "Cruise",
  "All-Inclusive Resort",
  "Custom / Land Itinerary",
  "Luxury Escape",
  "Adults Only",
  "Adventure",
  "Relaxation",
  "Other",
];
const AMENITIES = [
  "Ocean View / Balcony",
  "Adults Only",
  "Family-Friendly",
  "Spa",
  "Fine Dining",
  "Nightlife",
  "Excursions / Activities",
  "Accessible Room Needs",
];
const OCCASIONS = ["Birthday", "Anniversary", "Honeymoon", "Girls Trip", "Other"];
const HELP = ["Excursions", "Dining reservations", "Travel insurance", "VIP upgrades"];
const TRIP_LENGTHS = ["1–5 Nights", "6–10 Nights", "11–14 Nights", "14+ Nights"];
const BUDGETS = [
  "$2,500 – $5,000",
  "$5,000 – $10,000",
  "$10,000 – $15,000",
  "$15,000 – $20,000",
  "$20,000+",
  "Prefer not to say",
];
const BEST_TIME_DAY = ["Weekday", "Weekend", "Any Day"];
const BEST_TIME_WINDOW = ["8 am – 12 pm", "12 – 5 pm", "5 – 8 pm", "Anytime"];

const SERVICE_TIERS = [
  {
    key: "Easy Escape",
    icon: "✈️",
    price: "Complimentary",
    tagline: "Simple. Straightforward. Supported.",
    bestFor: "Quick getaways & packaged trips",
    features: {
      "Cruise & resort bookings": true,
      "Preferred supplier perks": true,
      "Basic itinerary organization": true,
      "Email support": true,
      "Personalized destination picks": false,
      "Hand-selected itinerary options": false,
      "Flights & logistics guidance": false,
      "Ongoing support during travel": false,
      "Concierge-level itinerary design": false,
      "End-to-end trip management": false,
      "Priority access & exclusive amenities": false,
      "Dining, spa & experience reservations": false,
      "Real-time support while abroad": false,
    },
  },
  {
    key: "Elevated Escape",
    icon: "🌍",
    price: "from $149",
    tagline: "Personalized. Thoughtful. Seamless.",
    bestFor: "Multi-stop or bucket-list trips",
    features: {
      "Cruise & resort bookings": true,
      "Preferred supplier perks": true,
      "Basic itinerary organization": true,
      "Email support": true,
      "Personalized destination picks": true,
      "Hand-selected itinerary options": true,
      "Flights & logistics guidance": true,
      "Ongoing support during travel": true,
      "Concierge-level itinerary design": false,
      "End-to-end trip management": false,
      "Priority access & exclusive amenities": false,
      "Dining, spa & experience reservations": false,
      "Real-time support while abroad": false,
    },
  },
  {
    key: "Signature Escape",
    icon: "🥂",
    price: "from $349",
    tagline: "Curated. Elevated. Effortless.",
    bestFor: "Luxury & milestone escapes",
    isFeatured: true,
    features: {
      "Cruise & resort bookings": true,
      "Preferred supplier perks": true,
      "Basic itinerary organization": true,
      "Email support": true,
      "Personalized destination picks": true,
      "Hand-selected itinerary options": true,
      "Flights & logistics guidance": true,
      "Ongoing support during travel": true,
      "Concierge-level itinerary design": true,
      "End-to-end trip management": true,
      "Priority access & exclusive amenities": true,
      "Dining, spa & experience reservations": true,
      "Real-time support while abroad": true,
    },
  },
];
const COMPARISON_FEATURES = Object.keys(SERVICE_TIERS[0].features);

function Checkboxes({ options, value = [], onChange, name, testidPrefix }) {
  const toggle = (opt) => {
    const next = value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt];
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`checkbox-group-${name}`}>
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            className={`px-5 h-11 text-[12px] uppercase tracking-[0.2em] border transition-colors inline-flex items-center gap-2 ${
              active ? "bg-ink text-ivory border-ink" : "bg-transparent text-ink border-hairline hover:border-ink"
            }`}
            data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span
              aria-hidden="true"
              className={`inline-block w-3 text-center transition-opacity ${active ? "opacity-100" : "opacity-0"}`}
            >
              ✓
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Radios({ options, value, onChange, name, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`radio-group-${name}`}>
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-5 h-11 text-[12px] uppercase tracking-[0.2em] border transition-colors ${
            value === opt ? "bg-gold text-ink border-gold" : "bg-transparent text-ink border-hairline hover:border-ink"
          }`}
          data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

const initialData = () => ({
  full_name: "",
  date_of_birth: "",
  email: "",
  phone: "",
  preferred_contact: "",
  best_time_day: "",
  best_time_window: "",
  destinations: "",
  travel_date_start: "",
  travel_date_end: "",
  trip_length: "",
  departure_airports: "",
  dates_flexible: "Exact",
  num_adults: "",
  num_children: "",
  travelers: [{ name: "", dob: "" }],
  citizenship: "",
  passports_valid: "",
  passport_expirations: "",
  trip_types: [],
  accommodation_style: "",
  bed_configuration: "",
  rooms_needed: "",
  amenities: [],
  budget_range: "",
  budget_includes_airfare: "",
  flexible_for_value: "",
  flights_needed: "",
  preferred_airline: "",
  seat_preference: "",
  airport_transfers: "",
  rental_car: "",
  rental_car_preference: "",
  experience_description: "",
  must_do_activities: "",
  special_occasions: [],
  help_with: [],
  service_level_interest: "",
});

export default function PlanEscape() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [data, setData] = useState(initialData());
  const formTopRef = useRef(null);

  const scrollToFormTop = () => {
    const el = formTopRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Pre-select service tier from query param (e.g., ?tier=elevated)
  useEffect(() => {
    const tier = (searchParams.get("tier") || "").toLowerCase();
    const map = { easy: "Easy Escape", elevated: "Elevated Escape", signature: "Signature Escape" };
    if (map[tier]) {
      setData((d) => ({ ...d, service_level_interest: map[tier] }));
    }
  }, [searchParams]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Auto-grow travelers array as adults + children counts change
  useEffect(() => {
    const target = (Number(data.num_adults) || 0) + (Number(data.num_children) || 0);
    if (target === 0) return;
    setData((d) => {
      const current = d.travelers || [];
      if (current.length === target) return d;
      const next = current.slice(0, target);
      while (next.length < target) next.push({
        name: "",
        dob: "",
        passport_number: "",
        passport_country: "",
        passport_issue_date: "",
        passport_expiration: "",
        address_same_as_main: true,
        address_street: "",
        address_city: "",
        address_state: "",
        address_zip: "",
        address_country: "",
      });
      return { ...d, travelers: next };
    });
  }, [data.num_adults, data.num_children]);

  // Step definitions — Flights step is conditional
  const STEPS = useMemo(() => {
    const list = [
      { key: "contact", title: "Contact", blurb: "Let's start with a few essentials." },
      { key: "travel", title: "Travel", blurb: "Where to, and when?" },
      { key: "travelers", title: "Travelers", blurb: "Who's escaping with you?" },
      { key: "style", title: "Style", blurb: "Set the tone." },
      { key: "budget", title: "Budget", blurb: "Help me tailor to value." },
    ];
    if (data.budget_includes_airfare !== "No") {
      list.push({ key: "flights", title: "Flights", blurb: "How you'll get there." });
    }
    list.push({ key: "experience", title: "Experience", blurb: "The finishing touches." });
    return list;
  }, [data.budget_includes_airfare]);

  const progress = ((step + 1) / STEPS.length) * 100;
  const currentKey = STEPS[step]?.key;

  // --- Validation ---
  const validateStep = () => {
    if (currentKey === "contact") {
      if (!data.full_name.trim()) return toast.error("Please enter your full name."), false;
      if (data.full_name.length > 200) return toast.error("Name is too long."), false;
      if (!isValidEmail(data.email)) return toast.error("Please enter a valid email address."), false;
    }
    if (currentKey === "travel") {
      // dates_flexible defaults to "Exact" via calendar chip; no explicit validation needed
    }
    return true;
  };

  // --- Draft save (partial lead capture) ---
  const saveDraftIfNeeded = async () => {
    if (draftId) {
      // already saved; update with latest fields
      try {
        await api.put(`/inquiries/travel/${draftId}`, {
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
          date_of_birth: data.date_of_birth,
          preferred_contact: data.preferred_contact,
          best_time_day: data.best_time_day,
          best_time_window: data.best_time_window,
        });
      } catch {
        /* non-fatal */
      }
      return;
    }
    try {
      const res = await api.post("/inquiries/travel/start", {
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        date_of_birth: data.date_of_birth,
        preferred_contact: data.preferred_contact,
        best_time_day: data.best_time_day,
        best_time_window: data.best_time_window,
      });
      setDraftId(res.data.id);
    } catch {
      // Best effort — don't block the user
    }
  };

  const next = async () => {
    if (!validateStep()) return;
    if (currentKey === "contact") await saveDraftIfNeeded();
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      setTimeout(scrollToFormTop, 30);
    } else {
      submit();
    }
  };
  const back = () => {
    if (step > 0) {
      setStep(step - 1);
      setTimeout(scrollToFormTop, 30);
    }
  };

  const skip = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      setTimeout(scrollToFormTop, 30);
    } else {
      submit();
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        total_travelers: data.travelers.filter((t) => t.name || t.dob).length
          ? String(data.travelers.filter((t) => t.name || t.dob).length)
          : data.num_adults
          ? String(Number(data.num_adults || 0) + Number(data.num_children || 0))
          : "",
      };
      if (draftId) {
        await api.put(`/inquiries/travel/${draftId}`, { ...payload, status: "new" });
      } else {
        await api.post("/inquiries/travel", payload);
      }
      setDone(true);
      toast.success("Inquiry received — I'll be in touch soon.");
    } catch (err) {
      // Surface a more helpful error message when the backend validates the payload
      let msg = "Something didn't send. Please try again.";
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && Array.isArray(detail) && detail[0]?.loc) {
        const field = detail[0].loc.slice(1).join(".") || "a field";
        msg = `That's a lot to say — please shorten "${field}" a little and try again.`;
      } else if (status >= 500) {
        msg = "Our server had a hiccup. Please try again in a moment.";
      }
      // eslint-disable-next-line no-console
      console.error("Plan Escape submit failed:", status, detail || err?.message || err);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[80vh] py-32 px-6 lg:px-10 flex items-center bg-[#F3EDE1]" data-testid="plan-success">
        <div className="max-w-3xl mx-auto text-center">
          <Check size={54} strokeWidth={1} className="mx-auto text-gold mb-8" />
          <div className="star-sep justify-center mb-6"><span>Received</span></div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Your escape<br /><span className="italic text-gold">is in motion.</span>
          </h1>
          <p className="mt-8 text-lg text-muted font-light leading-relaxed">
            Thank you, {(data.full_name || "").split(" ")[0]}. I'll review everything and get back to you within 24–48 hours with next steps.
          </p>
          <Link to="/" className="mt-10 inline-flex items-center gap-3 h-14 px-10 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em]" data-testid="plan-return-home">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-plan-escape">
      <section className="py-24 px-6 lg:px-10 bg-[#F3EDE1]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8">Plan Your Escape</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98]" data-testid="plan-title">
              Let's design something<br /><span className="italic text-gold">unforgettable.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={120}>
            <p className="text-lg text-muted leading-relaxed font-light">
              Share your vision — in detail or just a spark. This form helps me design a journey that fits you, not a template.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Progress rail */}
          <aside className="lg:col-span-3" data-testid="plan-rail">
            <div className="sticky top-28">
              <div className="text-xs uppercase tracking-[0.28em] text-gold mb-6">Step {step + 1} of {STEPS.length}</div>
              <div className="h-[2px] w-full bg-hairline relative mb-8">
                <div className="absolute top-0 left-0 h-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <ol className="space-y-4">
                {STEPS.map((s, i) => (
                  <li key={s.key} className={`flex items-start gap-3 text-sm ${i === step ? "text-ink" : i < step ? "text-muted" : "text-muted/50"}`}>
                    <span className={`font-serif text-2xl leading-none ${i === step ? "text-gold" : i < step ? "text-teal" : "text-muted/40"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="uppercase tracking-[0.22em] text-[11px]">{s.title}</div>
                      <div className="font-light text-xs italic opacity-70">{s.blurb}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-9">
            <div className="max-w-3xl" ref={formTopRef}>
              <div className="font-serif text-4xl lg:text-5xl mb-10 text-ink">
                <span className="text-gold text-xl align-top mr-3 font-sans tracking-[0.3em]">{String(step + 1).padStart(2, "0")}</span>
                {STEPS[step].title}
              </div>

              {/* STEP: Contact */}
              {currentKey === "contact" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="step-contact">
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Full Name (as on passport) *</label>
                    <input
                      className="field-editorial"
                      value={data.full_name}
                      onChange={(e) => set("full_name", e.target.value.slice(0, 200))}
                      maxLength={200}
                      data-testid="plan-full-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Date of Birth</label>
                    <DateField
                      value={data.date_of_birth}
                      onChange={(v) => set("date_of_birth", v)}
                      minYear={1920}
                      maxYear={new Date().getFullYear()}
                      testId="plan-dob"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Email *</label>
                    <input
                      className="field-editorial"
                      type="email"
                      value={data.email}
                      onChange={(e) => set("email", e.target.value.slice(0, 200))}
                      maxLength={200}
                      data-testid="plan-email"
                      required
                    />
                    {data.email && !isValidEmail(data.email) && (
                      <p className="text-xs text-red-600 mt-2">Please enter a valid email address.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Phone</label>
                    <input
                      className="field-editorial"
                      type="tel"
                      value={data.phone}
                      onChange={(e) => set("phone", formatPhone(e.target.value))}
                      placeholder="555-555-5555"
                      inputMode="tel"
                      maxLength={20}
                      data-testid="plan-phone"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Preferred Method of Contact</label>
                    <Radios options={["Call", "Text", "Email"]} value={data.preferred_contact} onChange={(v) => set("preferred_contact", v)} name="contact-method" testidPrefix="plan-contact-method" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Best Day</label>
                    <Radios options={BEST_TIME_DAY} value={data.best_time_day} onChange={(v) => set("best_time_day", v)} name="best-time-day" testidPrefix="plan-best-time-day" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Best Time</label>
                    <Radios options={BEST_TIME_WINDOW} value={data.best_time_window} onChange={(v) => set("best_time_window", v)} name="best-time-window" testidPrefix="plan-best-time-window" />
                  </div>

                  {/* Main Mailing Address */}
                  <div className="sm:col-span-2 pt-6 border-t border-hairline">
                    <div className="text-xs uppercase tracking-[0.28em] text-gold mb-1">Main Mailing Address</div>
                    <p className="text-xs text-muted font-light mb-5">Used as the default for all travelers — you can override per-traveler later.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                      <div className="sm:col-span-6">
                        <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Street Address</label>
                        <input
                          className="field-editorial"
                          value={data.mailing_street}
                          onChange={(e) => set("mailing_street", e.target.value.slice(0, 200))}
                          maxLength={200}
                          data-testid="plan-mailing-street"
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">City</label>
                        <input
                          className="field-editorial"
                          value={data.mailing_city}
                          onChange={(e) => set("mailing_city", e.target.value.slice(0, 100))}
                          maxLength={100}
                          data-testid="plan-mailing-city"
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">State</label>
                        <input
                          className="field-editorial"
                          value={data.mailing_state}
                          onChange={(e) => set("mailing_state", e.target.value.slice(0, 100))}
                          maxLength={100}
                          data-testid="plan-mailing-state"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Zip / Postal</label>
                        <input
                          className="field-editorial"
                          value={data.mailing_zip}
                          onChange={(e) => set("mailing_zip", e.target.value.slice(0, 40))}
                          maxLength={40}
                          data-testid="plan-mailing-zip"
                        />
                      </div>
                      <div className="sm:col-span-6">
                        <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Country</label>
                        <input
                          className="field-editorial"
                          value={data.mailing_country}
                          onChange={(e) => set("mailing_country", e.target.value.slice(0, 100))}
                          maxLength={100}
                          data-testid="plan-mailing-country"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP: Travel */}
              {currentKey === "travel" && (
                <div className="grid grid-cols-1 gap-8" data-testid="step-travel">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Desired Destination(s)</label>
                    <input
                      className="field-editorial"
                      placeholder="e.g., Santorini, Amalfi Coast, Maldives, or 'somewhere warm'"
                      value={data.destinations}
                      onChange={(e) => set("destinations", e.target.value.slice(0, 500))}
                      maxLength={500}
                      data-testid="plan-destinations"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Travel Dates</label>
                      <DateRangeField
                        value={{ start: data.travel_date_start, end: data.travel_date_end }}
                        onChange={({ start, end }) => { set("travel_date_start", start); set("travel_date_end", end); }}
                        flexibility={data.dates_flexible || "Exact"}
                        onFlexibilityChange={(v) => set("dates_flexible", v)}
                        testId="plan-date-range"
                      />
                      <p className="text-[10px] uppercase tracking-[0.22em] text-teal mt-2">
                        {data.dates_flexible && data.dates_flexible !== "Exact" ? `Dates flexible: ${data.dates_flexible}` : "Exact dates"}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Trip Length</label>
                      <Radios options={TRIP_LENGTHS} value={data.trip_length} onChange={(v) => set("trip_length", v)} name="trip-length" testidPrefix="plan-trip-length" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Departure Airport(s)</label>
                    <AirportField
                      value={data.departure_airports}
                      onChange={(v) => set("departure_airports", v)}
                      testId="plan-airports"
                    />
                  </div>
                </div>
              )}

              {/* STEP: Travelers */}
              {currentKey === "travelers" && (
                <div className="space-y-8" data-testid="step-travelers">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Adults (18+)</label>
                      <select
                        className="field-editorial appearance-none cursor-pointer pr-8"
                        style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, #C8A951 50%), linear-gradient(135deg, #C8A951 50%, transparent 50%)", backgroundPosition: "right 10px top 50%, right 4px top 50%", backgroundSize: "6px 6px, 6px 6px", backgroundRepeat: "no-repeat" }}
                        value={data.num_adults}
                        onChange={(e) => set("num_adults", e.target.value)}
                        data-testid="plan-adults"
                      >
                        <option value="">Select</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Children (under 18)</label>
                      <select
                        className="field-editorial appearance-none cursor-pointer pr-8"
                        style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, #C8A951 50%), linear-gradient(135deg, #C8A951 50%, transparent 50%)", backgroundPosition: "right 10px top 50%, right 4px top 50%", backgroundSize: "6px 6px, 6px 6px", backgroundRepeat: "no-repeat" }}
                        value={data.num_children}
                        onChange={(e) => set("num_children", e.target.value)}
                        data-testid="plan-children"
                      >
                        <option value="">Select</option>
                        {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {data.travelers && data.travelers.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-xs uppercase tracking-[0.24em] text-muted">Each Traveler · Passport & Address</label>
                      <button
                        type="button"
                        onClick={() => set("travelers", [...data.travelers, {
                          name: "",
                          dob: "",
                          passport_number: "",
                          passport_country: "",
                          passport_issue_date: "",
                          passport_expiration: "",
                          address_same_as_main: true,
                          address_street: "",
                          address_city: "",
                          address_state: "",
                          address_zip: "",
                          address_country: "",
                        }])}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.24em] text-gold hover:text-goldDeep"
                        data-testid="plan-add-traveler"
                      >
                        <Plus size={12} strokeWidth={1.5} /> Add Traveler
                      </button>
                    </div>
                    <div className="space-y-6">
                      {data.travelers.map((t, i) => {
                        const updateTraveler = (patch) => {
                          const next = [...data.travelers];
                          next[i] = { ...next[i], ...patch };
                          set("travelers", next);
                        };
                        return (
                          <div key={i} className="border border-hairline bg-ivory p-5 sm:p-6" data-testid={`plan-traveler-row-${i}`}>
                            <div className="flex items-center justify-between mb-4">
                              <div className="text-[11px] uppercase tracking-[0.28em] text-gold">Traveler {i + 1}</div>
                              {data.travelers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => set("travelers", data.travelers.filter((_, x) => x !== i))}
                                  className="text-muted hover:text-red-600 text-[11px] uppercase tracking-[0.24em] inline-flex items-center gap-1"
                                  aria-label="Remove traveler"
                                  data-testid={`plan-remove-traveler-${i}`}
                                >
                                  <X size={13} strokeWidth={1.5} /> Remove
                                </button>
                              )}
                            </div>

                            {/* Name & DOB */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                              <div>
                                <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Full Name (as on passport)</label>
                                <input
                                  className="field-editorial"
                                  value={t.name}
                                  onChange={(e) => updateTraveler({ name: e.target.value.slice(0, 200) })}
                                  maxLength={200}
                                  data-testid={`plan-traveler-name-${i}`}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Date of Birth</label>
                                <DateField
                                  value={t.dob}
                                  onChange={(v) => updateTraveler({ dob: v })}
                                  minYear={1920}
                                  maxYear={new Date().getFullYear()}
                                  testId={`plan-traveler-dob-${i}`}
                                />
                              </div>
                            </div>

                            {/* Passport */}
                            <div className="pt-5 border-t border-hairline">
                              <div className="text-[10px] uppercase tracking-[0.24em] text-teal mb-3">Passport Details</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Passport Number</label>
                                  <input
                                    className="field-editorial"
                                    value={t.passport_number || ""}
                                    onChange={(e) => updateTraveler({ passport_number: e.target.value.slice(0, 30) })}
                                    maxLength={30}
                                    data-testid={`plan-traveler-passport-${i}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Issuing Country</label>
                                  <input
                                    className="field-editorial"
                                    value={t.passport_country || ""}
                                    onChange={(e) => updateTraveler({ passport_country: e.target.value.slice(0, 100) })}
                                    maxLength={100}
                                    placeholder="e.g., United States"
                                    data-testid={`plan-traveler-passport-country-${i}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Issue Date</label>
                                  <DateField
                                    value={t.passport_issue_date || ""}
                                    onChange={(v) => updateTraveler({ passport_issue_date: v })}
                                    minYear={1980}
                                    maxYear={new Date().getFullYear()}
                                    testId={`plan-traveler-passport-issue-${i}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Expiration Date</label>
                                  <DateField
                                    value={t.passport_expiration || ""}
                                    onChange={(v) => updateTraveler({ passport_expiration: v })}
                                    minYear={new Date().getFullYear()}
                                    maxYear={new Date().getFullYear() + 15}
                                    testId={`plan-traveler-passport-exp-${i}`}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Mailing Address (only for travelers beyond the first) */}
                            {i > 0 && (
                              <div className="pt-5 mt-5 border-t border-hairline">
                                <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={t.address_same_as_main !== false}
                                    onChange={(e) => updateTraveler({ address_same_as_main: e.target.checked })}
                                    className="w-4 h-4 accent-gold"
                                    data-testid={`plan-traveler-address-same-${i}`}
                                  />
                                  Uses main mailing address
                                </label>
                                {t.address_same_as_main === false && (
                                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mt-4">
                                    <div className="sm:col-span-6">
                                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Street</label>
                                      <input
                                        className="field-editorial"
                                        value={t.address_street || ""}
                                        onChange={(e) => updateTraveler({ address_street: e.target.value.slice(0, 200) })}
                                        maxLength={200}
                                        data-testid={`plan-traveler-street-${i}`}
                                      />
                                    </div>
                                    <div className="sm:col-span-3">
                                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">City</label>
                                      <input
                                        className="field-editorial"
                                        value={t.address_city || ""}
                                        onChange={(e) => updateTraveler({ address_city: e.target.value.slice(0, 100) })}
                                        maxLength={100}
                                        data-testid={`plan-traveler-city-${i}`}
                                      />
                                    </div>
                                    <div className="sm:col-span-1">
                                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">State</label>
                                      <input
                                        className="field-editorial"
                                        value={t.address_state || ""}
                                        onChange={(e) => updateTraveler({ address_state: e.target.value.slice(0, 100) })}
                                        maxLength={100}
                                        data-testid={`plan-traveler-state-${i}`}
                                      />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Zip</label>
                                      <input
                                        className="field-editorial"
                                        value={t.address_zip || ""}
                                        onChange={(e) => updateTraveler({ address_zip: e.target.value.slice(0, 40) })}
                                        maxLength={40}
                                        data-testid={`plan-traveler-zip-${i}`}
                                      />
                                    </div>
                                    <div className="sm:col-span-6">
                                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Country</label>
                                      <input
                                        className="field-editorial"
                                        value={t.address_country || ""}
                                        onChange={(e) => updateTraveler({ address_country: e.target.value.slice(0, 100) })}
                                        maxLength={100}
                                        data-testid={`plan-traveler-country-${i}`}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Citizenship</label>
                      <input
                        className="field-editorial"
                        value={data.citizenship}
                        onChange={(e) => set("citizenship", e.target.value.slice(0, 200))}
                        maxLength={200}
                        data-testid="plan-citizenship"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Passports Valid?</label>
                      <Radios options={["Yes", "No", "Need Assistance"]} value={data.passports_valid} onChange={(v) => set("passports_valid", v)} name="passports" testidPrefix="plan-passports" />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP: Style */}
              {currentKey === "style" && (
                <div className="space-y-10" data-testid="step-style">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Type of Trip (select all that apply)</label>
                    <Checkboxes options={TRIP_TYPES} value={data.trip_types} onChange={(v) => set("trip_types", v)} name="trip-types" testidPrefix="plan-trip-type" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Accommodation Style</label>
                    <Radios options={["Budget-Friendly", "Mid-Level Comfort", "Luxury / High-End"]} value={data.accommodation_style} onChange={(v) => set("accommodation_style", v)} name="accom" testidPrefix="plan-accom" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Bed Configuration</label>
                      <Radios options={["King", "Queen", "Twins"]} value={data.bed_configuration} onChange={(v) => set("bed_configuration", v)} name="beds" testidPrefix="plan-beds" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Rooms Needed</label>
                      <select
                        className="field-editorial appearance-none cursor-pointer pr-8 max-w-[140px]"
                        style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, #C8A951 50%), linear-gradient(135deg, #C8A951 50%, transparent 50%)", backgroundPosition: "right 10px top 50%, right 4px top 50%", backgroundSize: "6px 6px, 6px 6px", backgroundRepeat: "no-repeat" }}
                        value={data.rooms_needed}
                        onChange={(e) => set("rooms_needed", e.target.value)}
                        data-testid="plan-rooms"
                      >
                        <option value="">Select</option>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Must-Have Amenities</label>
                    <Checkboxes options={AMENITIES} value={data.amenities} onChange={(v) => set("amenities", v)} name="amenities" testidPrefix="plan-amenity" />
                  </div>
                </div>
              )}

              {/* STEP: Budget */}
              {currentKey === "budget" && (
                <div className="space-y-10" data-testid="step-budget">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Estimated Budget (total trip)</label>
                    <Radios options={BUDGETS} value={data.budget_range} onChange={(v) => set("budget_range", v)} name="budget" testidPrefix="plan-budget" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Does your budget include airfare?</label>
                    <Radios options={["Yes", "No", "Not Sure"]} value={data.budget_includes_airfare} onChange={(v) => set("budget_includes_airfare", v)} name="budget-air" testidPrefix="plan-budget-air" />
                    {data.budget_includes_airfare === "No" && (
                      <p className="text-xs text-muted italic mt-3">Flights step will be skipped — you'll handle flights separately.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Open to flexible options to maximize value?</label>
                    <Radios options={["Yes", "No"]} value={data.flexible_for_value} onChange={(v) => set("flexible_for_value", v)} name="flex-value" testidPrefix="plan-flex-value" />
                  </div>
                </div>
              )}

              {/* STEP: Flights */}
              {currentKey === "flights" && (
                <div className="space-y-10" data-testid="step-flights">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Flights included?</label>
                    <Radios options={["Yes", "No"]} value={data.flights_needed} onChange={(v) => set("flights_needed", v)} name="flights-needed" testidPrefix="plan-flights-needed" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Preferred Airline(s)</label>
                      <AirlineField
                        value={data.preferred_airline}
                        onChange={(v) => set("preferred_airline", v)}
                        testId="plan-airline"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Seat Preference</label>
                      <Radios options={["Economy", "Premium", "Business", "First Class"]} value={data.seat_preference} onChange={(v) => set("seat_preference", v)} name="seat" testidPrefix="plan-seat" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Airport Transfers?</label>
                      <Radios options={["Yes", "No"]} value={data.airport_transfers} onChange={(v) => set("airport_transfers", v)} name="transfers" testidPrefix="plan-transfers" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Rental Car?</label>
                      <Radios options={["Yes", "No"]} value={data.rental_car} onChange={(v) => set("rental_car", v)} name="rental" testidPrefix="plan-rental" />
                    </div>
                  </div>
                  {data.rental_car === "Yes" && (
                    <div>
                      <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Rental Car Preference</label>
                      <input
                        className="field-editorial"
                        placeholder="e.g., midsize SUV, automatic, brand preference"
                        value={data.rental_car_preference}
                        onChange={(e) => set("rental_car_preference", e.target.value.slice(0, 200))}
                        maxLength={200}
                        data-testid="plan-rental-preference"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* STEP: Experience */}
              {currentKey === "experience" && (
                <div className="space-y-10" data-testid="step-experience">
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">What kind of experience are you looking for?</label>
                    <textarea
                      rows="4"
                      className="field-editorial"
                      placeholder="Paint me a picture..."
                      value={data.experience_description}
                      onChange={(e) => set("experience_description", e.target.value.slice(0, 2000))}
                      maxLength={2000}
                      data-testid="plan-experience"
                    />
                    <p className="text-[10px] text-muted text-right mt-1">{data.experience_description.length}/2000</p>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-2 block">Must-do activities or bucket list items</label>
                    <textarea
                      rows="3"
                      className="field-editorial"
                      value={data.must_do_activities}
                      onChange={(e) => set("must_do_activities", e.target.value.slice(0, 2000))}
                      maxLength={2000}
                      data-testid="plan-must-do"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">Special occasion?</label>
                    <Checkboxes options={OCCASIONS} value={data.special_occasions} onChange={(v) => set("special_occasions", v)} name="occasions" testidPrefix="plan-occasion" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-3 block">I'd like help with</label>
                    <Checkboxes options={HELP} value={data.help_with} onChange={(v) => set("help_with", v)} name="help" testidPrefix="plan-help" />
                  </div>

                  {/* Service level comparison chart */}
                  <div data-testid="plan-service-level-section">
                    <label className="text-xs uppercase tracking-[0.24em] text-muted mb-4 block">Which Escape fits your trip?</label>
                    <p className="text-sm text-muted font-light mb-6 max-w-2xl">
                      Compare each level side-by-side and choose the one that feels right. Not sure? Skip this and I'll guide you.
                    </p>

                    {/* Desktop comparison chart */}
                    <div className="hidden lg:block border border-hairline bg-ivory" data-testid="plan-service-comparison-chart">
                      <div className="grid grid-cols-4">
                        {/* Header row */}
                        <div className="p-6 pt-9 border-b border-hairline border-r bg-[#FBF7EC]">
                          <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2">Feature</div>
                          <div className="text-xs text-muted font-light italic">Compare what's included at each level.</div>
                        </div>
                        {SERVICE_TIERS.map((t) => {
                          const active = data.service_level_interest === t.key;
                          return (
                            <div
                              key={t.key}
                              className={`p-6 pt-9 border-b border-hairline last:border-r-0 border-r relative ${
                                active ? "bg-[#FBF7EC]" : "bg-ivory"
                              }`}
                            >
                              {t.isFeatured && (
                                <div className="absolute top-0 left-0 right-0 bg-gold text-ink text-[9px] uppercase tracking-[0.28em] py-1 text-center">
                                  Most Popular
                                </div>
                              )}
                              <div className="text-2xl mb-2">{t.icon}</div>
                              <div className="font-serif text-2xl text-ink leading-tight">{t.key}</div>
                              <div className="text-[10px] uppercase tracking-[0.24em] text-gold mt-2">{t.price}</div>
                              <div className="text-[11px] uppercase tracking-[0.18em] text-teal mt-3">{t.tagline}</div>
                              <div className="text-xs text-muted font-light italic mt-2">{t.bestFor}</div>
                            </div>
                          );
                        })}

                        {/* Feature rows */}
                        {COMPARISON_FEATURES.map((feature, i) => (
                          <React.Fragment key={feature}>
                            <div className={`px-6 py-3 border-r border-hairline text-sm text-ink font-light ${i % 2 === 0 ? "bg-[#FBF7EC]/40" : "bg-ivory"}`}>
                              {feature}
                            </div>
                            {SERVICE_TIERS.map((t) => {
                              const active = data.service_level_interest === t.key;
                              const included = t.features[feature];
                              return (
                                <div
                                  key={t.key + feature}
                                  className={`px-6 py-3 border-r border-hairline last:border-r-0 flex items-center justify-center ${
                                    active ? "bg-[#FBF7EC]" : i % 2 === 0 ? "bg-[#FBF7EC]/40" : "bg-ivory"
                                  }`}
                                >
                                  {included ? (
                                    <Check size={16} strokeWidth={2} className="text-gold" />
                                  ) : (
                                    <span className="text-muted/40 text-lg leading-none">—</span>
                                  )}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}

                        {/* Select button row */}
                        <div className="p-6 border-r border-hairline bg-nightsky text-ivory">
                          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Ready?</div>
                          <div className="text-sm font-light text-ivory/80">Pick your escape level.</div>
                        </div>
                        {SERVICE_TIERS.map((t) => {
                          const active = data.service_level_interest === t.key;
                          return (
                            <div key={t.key + "-btn"} className="p-6 border-r border-hairline last:border-r-0 bg-nightsky">
                              <button
                                type="button"
                                onClick={() => set("service_level_interest", t.key)}
                                className={`w-full h-11 text-[11px] uppercase tracking-[0.24em] transition-colors inline-flex items-center justify-center gap-2 ${
                                  active
                                    ? "bg-gold text-ink"
                                    : "bg-transparent text-ivory border border-ivory/40 hover:bg-gold hover:text-ink hover:border-gold"
                                }`}
                                data-testid={`plan-service-level-${t.key.toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                {active ? (<><Check size={13} strokeWidth={2} /> Selected</>) : "Select"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Mobile stacked cards */}
                    <div className="lg:hidden space-y-4" data-testid="plan-service-comparison-mobile">
                      {SERVICE_TIERS.map((t) => {
                        const active = data.service_level_interest === t.key;
                        const included = COMPARISON_FEATURES.filter((f) => t.features[f]);
                        return (
                          <div
                            key={t.key}
                            className={`border ${active ? "border-gold" : "border-hairline"} bg-ivory p-5 relative ${
                              t.isFeatured ? "mt-4" : ""
                            }`}
                          >
                            {t.isFeatured && (
                              <div className="absolute -top-3 left-4 bg-gold text-ink text-[9px] uppercase tracking-[0.28em] px-3 py-1">
                                Most Popular
                              </div>
                            )}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div>
                                <div className="text-xl mb-1">{t.icon}</div>
                                <div className="font-serif text-xl text-ink">{t.key}</div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold mt-1">{t.price}</div>
                              </div>
                              {active && <Check size={18} strokeWidth={2} className="text-gold" />}
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-teal mb-1">{t.tagline}</div>
                            <div className="text-xs text-muted font-light italic mb-4">{t.bestFor}</div>
                            <ul className="space-y-1.5 mb-5">
                              {included.map((f) => (
                                <li key={f} className="text-xs text-ink/80 font-light flex items-start gap-2">
                                  <Check size={12} strokeWidth={2} className="text-gold mt-0.5 shrink-0" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                            <button
                              type="button"
                              onClick={() => set("service_level_interest", t.key)}
                              className={`w-full h-11 text-[11px] uppercase tracking-[0.24em] transition-colors inline-flex items-center justify-center gap-2 ${
                                active ? "bg-gold text-ink" : "bg-ink text-ivory hover:bg-teal"
                              }`}
                              data-testid={`plan-service-level-mobile-${t.key.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {active ? (<><Check size={13} strokeWidth={2} /> Selected</>) : "Select This Escape"}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Not sure option */}
                    <button
                      type="button"
                      onClick={() => set("service_level_interest", "Not Sure")}
                      className={`w-full mt-4 text-center p-5 border transition-colors ${
                        data.service_level_interest === "Not Sure" ? "border-gold bg-[#FBF7EC]" : "border-hairline hover:border-ink bg-ivory"
                      }`}
                      data-testid="plan-service-level-not-sure"
                    >
                      <div className="font-serif text-lg text-ink inline-flex items-center gap-2">
                        {data.service_level_interest === "Not Sure" && <Check size={16} strokeWidth={2} className="text-gold" />}
                        Not sure — help me decide.
                      </div>
                      <div className="text-xs text-muted font-light mt-1">I'll guide you to the right level based on your trip.</div>
                    </button>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="mt-14 pt-8 border-t border-hairline flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 h-12 px-5 text-[12px] uppercase tracking-[0.24em] text-ink hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="plan-back"
                >
                  <ArrowLeft size={14} strokeWidth={1.5} /> Back
                </button>
                <div className="flex items-center gap-3">
                  {step > 0 && step < STEPS.length - 1 && (
                    <button
                      type="button"
                      onClick={skip}
                      className="inline-flex items-center gap-2 h-12 px-5 text-[12px] uppercase tracking-[0.24em] text-muted hover:text-gold"
                      data-testid="plan-skip"
                    >
                      Skip
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={next}
                    disabled={submitting}
                    className="inline-flex items-center gap-3 h-12 px-8 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.24em] disabled:opacity-60"
                    data-testid="plan-next"
                  >
                    {submitting ? "Submitting..." : step === STEPS.length - 1 ? "Submit Inquiry" : "Continue"}
                    {step === STEPS.length - 1 ? <ArrowUpRight size={14} strokeWidth={1.5} /> : <ArrowRight size={14} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
